<template lang="pug" src="./groups.pug"></template>
<style lang="scss" src="./groups.scss" scoped></style>

<script>
import validate from '@/mixins/validate'
import scroll from '@/utils/scroll'
import { debounce } from 'debounce'

import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { numeric, required } from 'vuelidate/lib/validators'
import request from '@/utils/request'
import { localizeField } from '@/helpers/locales'

export default {
    filters: {
        hideForCustomSelection: function(value, selection) {
            return selection === 'custom' ? '-' : value
        },

        hideForDeliveryGroups: function(value, isDelivery) {
            return isDelivery ? '-' : value
        },

        specifiedEstablishment: function(item, t, locale) {
            return item.isDelivery ? (item.establishment ? item.establishment[localizeField('name', locale)] : t('$vuetify.pages.Groups.allEstablishments')) : '-'
        }
    },
    mixins: [validationMixin, validate],
    data: () => ({
        sorting: {
            rowsPerPage: 100
        },
        showEditor: false,
        removeDialog: false,
        showGroupStatistic: false,
        loadedStatistic: false,
        deliveryClients: [],
        removeGroupId: null,
        rewriteWarningOpen: false,
        editorData: {
            fromDate: null,
            toDate: null,
            deliveryAutomaticMode: true,
            subGroups: 1,
            bonusSystem: 'cashbackPercent',
            type: 'equal',
            clients: '',
            existSystem: '0',
            phones: [],
            phoneListEditor: '',
            custom: false,
            delivery: false
        },
        customInfo: null,
        search: null,
        brandSearch: null,
        deliveryBrandSearch: null,
        establishmentSearch: null,
        establishments: [],
        delivery: {
            brand: null,
            establishment: null,
            name: ''
        },
        custom: {
            name: null,
            phones: null,
            autoAddNewClients: false
        },
        disabled: false,
        filters: {
            name: null,
            type: null,
            valuesFrom: null,
            valuesTo: null,
            includeUnallocated: null,
            onlyUnallocated: null,
            accountId: null,
            used: null,
            fromDate: null,
            toDate: null,
            selection: null,
            bonusSystem: null
        },
        accountFilterSearch: null,
        statisticMenu: false,
        customDeliveryInfo: null,
        statisticsLoading: false
    }),
    validations: {
        custom: {
            name: {
                required
            },
            phones: {
                required
            },
            autoAddNewClients: {
                required
            }
        },
        delivery: {
            name: {
                required
            },
            brand: {}
        },
        editorData: {
            name: {
                required
            },
            subGroups: {
                numeric,
                min: (val) => {
                    if (!val) return true

                    return val > 0
                },
                max: (val) => {
                    if (!val) return true

                    return val < 6
                }
            },
            bonusSystem: {
                in: val => {
                    return ['cashbackPercent', 'sum', 'count'].includes(val)
                }
            },
            count: {
                required: function(val) {
                    if (this.editorData.type === 'range') {
                        return true
                    }

                    return !!val || Number.isInteger(val)
                },
                numeric,
                min: function(val) {
                    if (this.editorData.type === 'less') {
                        return val > 1
                    }

                    if (!val) return true

                    return val >= 0
                },
                max: (val) => {
                    if (!val) return true

                    return val <= 50
                }
            },
            fromDate: {
                required,
                max: function(val) {
                    if (!this.editorData.toDate) return true

                    return new Date(val) < new Date(this.editorData.toDate)
                }
            },
            toDate: {
                required,
                max: function(val) {
                    if (this.editorData.type === 'equal' && this.editorData.count == 0) {
                        return true
                    }

                    return new Date(val) < new Date().addDays(-1)
                }
            }
        }
    },
    computed: {
        ...mapState({
            isLoaded: (state) => state.groups.isLoaded,
            groups: (state) => state.groups.groups,
            groupStatistic: (state) => state.groups.groupStatistic,
            businessGroupStatistic: (state) => state.groups.businessGroupStatistic,
            pagination: (state) => state.groups.pagination,
            completedAccounts: (state) => state.app.completedAccounts,
            completedBrands: (state) => state.app.completedBrands,
            clientsCount: (state) => state.groups.clientsCount,
            appliedFilters: (state) => state.groups.filters
        }),
        headers() {
            return [
                { text: 'id', value: 'id' },
                { text: 'name', value: 'name' },
                { text: 'selection', value: 'selection' },
                { text: 'scope', value: 'isDelivery' },
                { text: 'bonusSystem', value: 'bonusSystem', sortable: false },
                { text: 'type', value: 'type' },
                { text: 'values', sortable: false },
                { text: 'includeUnallocated', value: '"includeUnallocated"' },
                { text: 'onlyUnallocated', value: '"onlyUnallocated"' },
                { text: 'fromDate', sortable: false },
                { text: 'toDate', sortable: false },
                { text: 'accountId', sortable: false },
                { text: 'establishment', sortable: false },
                { text: 'clients', value: 'clients' },
                { text: 'createdAt', value: '"createdAt"' },
                { text: 'used', sortable: false },
                { text: 'autoAdd', sortable: false },
                { text: 'actions', sortable: false }
            ].map(it => Object.assign(it, {
                text: this.$vuetify.lang.t(`$vuetify.pages.Groups.table.${it.text}`)
            }))
        },
        typeSelects: function() {
            const typeSelects = '$vuetify.pages.Groups.types'
            return ['equal', 'less', 'more', 'range'].map(it => ({
                text: this.$vuetify.t(`${typeSelects}.${it}`),
                value: it
            }))
        },
        bonusSystemOptions: function() {
            const translationPath = '$vuetify.pages.Groups.bonusSystems'
            return ['cashbackPercent', 'count', 'sum'].map(it => ({
                text: this.$vuetify.t(`${translationPath}.${it}`),
                value: it
            }))
        },
        selectionTypes: function() {
            const typeSelects = '$vuetify.pages.Groups.selections'
            return ['custom', 'condition'].map(it => ({
                text: this.$vuetify.t(`${typeSelects}.${it}`),
                value: it
            }))
        },
        booleanSelect: function() {
            return [true, false].map(it => ({
                text: this.$vuetify.t(`$vuetify.${it}`),
                value: it
            }))
        },
        table_bonusSystemOptions: function() {
            const translationPath = '$vuetify.pages.Groups.table.bonusSystems'
            return ['cashbackPercent', 'count', 'sum'].map(it => ({
                text: this.$vuetify.t(`${translationPath}.${it}`),
                value: it
            }))
        },
        editorTitle() {
            const type = this.editorData.id ? 'edit' : 'create'

            return this.$vuetify.lang.t(`$vuetify.pages.Groups.editor.${type}`)
        },
        fromDateMax() {
            return new Date().addDays(-5).toJSON()
        },
        toDateMin() {
            return this.editorData.fromDate && new Date(this.editorData.fromDate).toJSON()
        },
        toDateMax() {
            let days = 100

            if (+this.editorData.count > 0 || ['range', 'less', 'more'].includes(this.editorData.type)) {
                days = -1
            }

            return new Date().addDays(days).toJSON()
        },
        accounts() {
            if (this.editorData.account) {
                return this.completedAccounts.concat([this.editorData.account])
            }

            return this.completedAccounts
        },
        brands() {
            if (this.editorData.brand) {
                return this.completedBrands.concat([this.editorData.brand])
            }

            return this.completedBrands
        },
        validRange() {
            return this.editorData.range && Number.isInteger(this.editorData.range[0]) &&
          Number.isInteger(this.editorData.range[1]) && this.editorData.range[0] !== this.editorData.range[1]
        },
        blockAction() {
            return this.editorData.type == 'range' && !this.validRange
        },
        hasCountValue() {
            if (this.editorData.type == 'range') {
                return !!this.editorData.range
            }

            return Number.isInteger(parseInt(this.editorData.count))
        },
        showClientsCount() {
            return Number.isInteger(this.clientsCount)
        },
        leaderStatistic() {
            return this.groupStatistic && this.groupStatistic.accounts
        },
        businessStatistic() {
            return this.businessGroupStatistic && this.businessGroupStatistic.accounts
        },
        clientsWithGroup() {
            return this.groupStatistic && +this.groupStatistic.allWithGroup + +this.groupStatistic.withGroupUnallocated
        },
        clientsWithoutGroup() {
            return this.groupStatistic && +this.groupStatistic.allWithoutGroup + +this.groupStatistic.withoutGroupUnallocated
        }
    },
    watch: {
        sorting: {
            handler() {
                this.getGroups()
            },
            deep: true
        },
        search: debounce(function(query) {
            if (typeof query != 'string') return

            this.$store.dispatch('getCompletedAccounts', {
                query
            })
        }, 500),
        brandSearch: debounce(function(query) {
            if (typeof query != 'string') return

            this.$store.dispatch('getCompletedBrands', {
                query
            })
        }, 500),
        deliveryBrandSearch: debounce(function(query) {
            if (typeof query != 'string') return

            this.$store.dispatch('getCompletedBrands', {
                query
            })
        }, 500),
        accountFilterSearch: debounce(function(query) {
            if (typeof query != 'string') return

            this.$store.dispatch('getCompletedAccounts', {
                query
            })
        }, 500),
        showEditor() {
            this.$store.commit('groups/SET_CLIENTS_COUNT', null)
        },
        'editorData.delivery'() {
            if (this.editorData.delivery && this.editorData.custom) {
                this.editorData.custom = false
            }
        },
        'delivery.brand'(current) {
            if (!current) {
                this.delivery.establishment = null
            } else {
                this.custom.autoAddNewClients = false
            }
        },
        'editorData.custom'() {
            if (this.editorData.delivery && this.editorData.custom) {
                this.editorData.delivery = false
            }
        },
        editorData: {
            deep: true,
            handler() {
                if (!this.editorData.deliveryAutomaticMode) {
                    this.deliveryClients = []
                    this.customDeliveryInfo = null
                }
            }
        }
    },
    created: function() {
        this.filters = Object.assign({}, this.appliedFilters)
        this.getGroups()
        this.$store.dispatch('getCompletedAccounts', { query: '' })
        this.$store.dispatch('getCompletedBrands', { query: '' })
    },
    beforeRouteLeave: function(to, from, next) {
        this.$store.commit('groups/SET_PAGINATION', { page: 1, length: null })
        this.$store.commit('groups/RESET_FILTERS')
        next()
    },
    methods: {
        localizeField,
        getGroups(page = 1, isScrollEnable = false) {
            this.$store.dispatch('groups/getGroups', {
                page,
                column: this.sorting.sortBy,
                direction: this.sorting.descending ? 'desc' : 'asc',
                ...this.appliedFilters
            }).then(_ => {
                if (isScrollEnable) {
                    scroll(this.$refs.table)
                }
            })
        },
        resetValidationInfo() {
            if (this.customDeliveryInfo) {
                this.customDeliveryInfo = null
            }
        },
        resetFilters() {
            this.filters = Object.assign({}, {
                name: null,
                type: null,
                valuesFrom: null,
                valuesTo: null,
                includeUnallocated: null,
                onlyUnallocated: null,
                accountId: null,
                used: null,
                fromDate: new Date(+new Date() - 2419200000).toISOString().split('T')[0],
                toDate: new Date().toISOString().split('T')[0],
                selection: null,
                isDelivery: null,
                bonusSystem: null
            })
            this.$store.commit('groups/RESET_FILTERS')
            this.getGroups()
        },
        submit() {
            this.$store.commit('groups/SET_FILTERS', Object.assign({}, this.filters))
            this.getGroups()
        },
        changeFromDate(date) {
            this.filters.fromDate = date
        },
        changeToDate(date) {
            this.filters.toDate = date
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.getGroups(page, true)
            }
        },
        async editGroup(data) {
            this.$v.editorData.$reset()
            const {
                id, fromDate, toDate, name, account, includeUnallocated, existSystem, clients, deliveryAutomaticMode,
                values, type, onlyUnallocated, isDelivery, brand, bonusSystem, establishment, autoAddNewClients
            } = data

            this.editorData = isDelivery ? {
                delivery: isDelivery,
                bonusSystem,
                id,
                existSystem,
                clients,
                deliveryAutomaticMode,
                phoneListEditor: deliveryAutomaticMode ? null : (await request(`/group/${id}/clients`)).data.join('\n')
            } : {
                id, name, account, includeUnallocated, type, onlyUnallocated,
                count: values[0],
                range: values.length > 1 ? values : new Array(2).fill(0),
                fromDate: new Date(fromDate).format('{yyyy}-{MM}-{dd}'),
                toDate: new Date(toDate).format('{yyyy}-{MM}-{dd}')
            }

            if (brand) {
                await this.getEstablishmentsOfBrand(brand.id)
            }

            if (isDelivery) {
                this.delivery = {
                    name,
                    brand,
                    establishment
                }

                this.custom.autoAddNewClients = autoAddNewClients
            }

            this.showEditor = true
        },
        async checkDelivery() {
            if (this.disabled) {
                return
            }

            this.disabled = true

            await request({
                method: 'post',
                url: '/group/checkDelivery',
                data: { phones: this.editorData.phoneListEditor.split('\n'), bonusSystemType: this.editorData.bonusSystem }
            })
                .then(res => {
                    if (res.data) {
                        this.customDeliveryInfo = res.data.validationResult
                        this.deliveryClients = res.data.clients

                        if (res.data.validationResult.alreadyInGroup > 0) {
                            this.rewriteWarningOpen = true
                        }

                        this.disabled = false
                    }
                })
        },
        createGroup() {
            this.resetEditorData()
            this.$store.dispatch('getCompletedBrands', { query: '' })
            this.showEditor = true
        },
        async createOrEdit() {
            if (this.editorData.delivery) {
                if (this.$v.delivery.$invalid) {
                    this.$v.$touch()
                    return
                }
            } else if (this.$v.editorData.$invalid) {
                this.$v.$touch()
                return
            }

            if (this.editorData.delivery && !this.editorData.deliveryAutomaticMode && !this.customDeliveryInfo && this.deliveryClients.length === 0) {
                return this.checkDelivery()
            }

            this.disabled = true

            if (this.delivery.establishment && !this.establishments.find(e => e.id === this.delivery.establishment.id)) {
                this.delivery.establishment = null
            }

            const res = await this.$store.dispatch('groups/createOrEdit', this.getSaveData())
                .finally(() => {
                    this.disabled = false
                })

            if (res) {
                this.deliveryClients = []
                this.customDeliveryInfo = null
                this.showEditor = false

                if (!this.editorData.id || (this.editorData.id && this.editorData.delivery)) {
                    this.getGroups()
                }
            }
        },
        async check() {
            if (this.editorData.delivery) {
                if (this.$v.delivery.$invalid) {
                    this.$v.$touch()
                    return
                }
            } else if (this.$v.editorData.$invalid) {
                this.$v.$touch()
                return
            }

            this.disabled = true
            await this.$store.dispatch('groups/check', this.getSaveData())
                .finally(() => {
                    setTimeout(() => {
                        this.disabled = false
                    }, 400)
                })
        },
        changeEditorDataValue(field, value) {
            if (field == 'account' && this.editorData.includeUnallocated) {
                this.$set(this.editorData, 'includeUnallocated', false)
            }

            if (field == 'account' && this.editorData.onlyUnallocated) {
                this.$set(this.editorData, 'onlyUnallocated', false)
            }

            this.$set(this.editorData, field, value)
        },
        changeDeliveryValue(field, value) {
            this.$set(this.delivery, field, value)
        },
        chooseBrand(brand) {
            this.changeDeliveryValue('brand', brand)

            if (brand) {
                this.changeDeliveryValue('establishment', null)

                this.establishments = []

                this.getEstablishmentsOfBrand(brand.id)
            } else {
                this.$store.dispatch('getCompletedBrands', { query: '' })
            }
        },
        async getEstablishmentsOfBrand(brandId) {
            await request({
                url: `/brand/${brandId}/establishments`
            }).then(res => {
                if (res.data) {
                    this.establishments = res.data
                }
            })
        },
        resetEditorData() {
            this.$v.editorData.$reset()
            this.editorData = {
                fromDate: null,
                deliveryAutomaticMode: true,
                toDate: null,
                type: 'equal',
                subGroups: 1,
                name: null,
                establishmentId: null,
                brandId: null,
                bonusSystem: 'cashbackPercent',
                custom: false,
                phones: [],
                phoneListEditor: '',
                delivery: false
            }

            this.delivery = {
                brand: null,
                establishment: null,
                name: null
            }
        },
        getSaveData() {
            const {
                id, fromDate, toDate, name, account, includeUnallocated, bonusSystem,
                subGroups, type, count, range, onlyUnallocated, delivery: isDelivery
            } = this.editorData

            return isDelivery ? {
                name: this.delivery.name,
                clients: this.deliveryClients,
                autoAddNewClients: this.custom.autoAddNewClients,
                isDelivery,
                isTimeLimitedBonus: bonusSystem === 'count',
                isReachAmountBonus: bonusSystem === 'sum',
                brandId: (this.delivery.brand && this.delivery.brand.id) || null,
                establishmentId: (this.delivery.establishment && this.delivery.establishment.id) || null,
                id
            } : {
                fromDate: new Date(fromDate).format('{yyyy}-{MM}-{dd}'),
                toDate: new Date(toDate).format('{yyyy}-{MM}-{dd}'),
                accountId: account && account.id,
                values: type === 'range' ? range : [count],
                subGroups: +subGroups,
                name, includeUnallocated, id, type, onlyUnallocated, isDelivery
            }
        },
        canEdit(count) {
            return +(count || 0) == 0
        },
        openRemoveDialog(id) {
            this.removeGroupId = id
            this.removeDialog = true
        },
        async exportToExcel(id) {
            if (this.disabled) return

            this.disabled = true

            await this.$store.dispatch('groups/exportClientsToExcel', id)
                .finally(() => {
                    this.disabled = false
                })
        },
        async acceptRemove() {
            if (this.disabled) return

            this.disabled = true

            const res = await this.$store.dispatch('groups/removeGroup', this.removeGroupId)
                .finally(() => {
                    this.disabled = false
                })

            if (res) {
                this.removeDialog = false
                this.getGroups()
            }
        },
        getGroupStatistic() {
            this.$store.dispatch('groups/statistic').then(_ => {
                this.showGroupStatistic = true
            })
        },
        expandGroupStatistic(leaderId) {
            if (this.businessGroupStatistic && this.businessGroupStatistic.leaderId == leaderId) {
                return
            }

            if (this.statisticsLoading) return

            this.statisticsLoading = true

            this.$store.dispatch('groups/statistic', leaderId).finally(() => {
                this.$nextTick(() => {
                    this.statisticsLoading = false
                })
            })
        },
        async createCustom() {
            if (this.$v.custom.$invalid) {
                this.$v.custom.$touch()
                return
            }

            this.disabled = true

            const result = await this.$store.dispatch('groups/createCustom', {
                id: this.editorData.id,
                name: this.custom.name,
                phones: this.custom.phones,
                preview: !this.customInfo
            }).finally(() => {
                this.disabled = false
            })

            if (result && !this.customInfo) {
                this.customInfo = result
                return
            }

            if (result == 'OK') {
                this.getGroups()

                this.showEditor = false
                this.editorData.custom = false
                this.custom = {
                    name: null,
                    phones: null,
                    autoAddNewClients: false
                }
                this.customInfo = null

                this.$v.custom.$reset()
            }
        },

        changePhones() {
            if (this.customInfo) {
                this.customInfo = null
            }
        }
    }
}
</script>
